import React, { useCallback, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';

import Button from '@atlaskit/button/button';
import { Label } from '@atlaskit/form/label';
import { ErrorMessage } from '@atlaskit/form/messages';
import { Flex, Stack, Text } from '@atlaskit/primitives/compiled';
import RadioGroup from '@atlaskit/radio/radio-group';
import Textfield from '@atlaskit/textfield/text-field';

import { SidePanelBody, SidePanelFooter } from '@confluence/side-panel';

import type { RawAction } from '../__types__/SpaceSuggestion.ts';

type ActionType = RawAction['actionType'];

type SuggestionActionBuilderProps = {
	/** Suggestion the new action belongs to. Used to shape the generated `actionStepId`. */
	suggestionId: string;
	/**
	 * 1-based position of the new action within the suggestion, used to build the
	 * `{suggestionId}-{index}` `actionStepId` (see `__types__/SpaceSuggestion.ts`).
	 */
	nextActionIndex: number;
	/** Called with the fully-formed action when the form is submitted with valid input. */
	onAdd: (action: RawAction) => void;
	/** Returns to the review list without adding an action. */
	onBack: () => void;
};

const fieldMessages = defineMessages({
	contentId: {
		id: 'space-suggestions.action-builder.field.content-id',
		defaultMessage: 'Content ID',
		description: 'Label for the content ID input in the add-action form',
	},
	newTitle: {
		id: 'space-suggestions.action-builder.field.new-title',
		defaultMessage: 'New title',
		description: 'Label for the new title input when renaming content in the add-action form',
	},
	destinationAlias: {
		id: 'space-suggestions.action-builder.field.destination-alias',
		defaultMessage: 'Destination folder ID',
		description: 'Label for the move destination input in the add-action form',
	},
	folderTitle: {
		id: 'space-suggestions.action-builder.field.folder-title',
		defaultMessage: 'Folder name',
		description: 'Label for the new folder name input in the add-action form',
	},
	parentContentId: {
		id: 'space-suggestions.action-builder.field.parent-content-id',
		defaultMessage: 'Parent content ID',
		description: 'Label for the parent content input when creating a folder in the add-action form',
	},
});

/** Fields rendered per action type. Order controls top-to-bottom layout. */
const FIELDS_BY_TYPE: Record<ActionType, Array<{ key: string; label: MessageDescriptor }>> = {
	RENAME: [
		{ key: 'contentId', label: fieldMessages.contentId },
		{ key: 'title', label: fieldMessages.newTitle },
	],
	MOVE: [
		{ key: 'contentId', label: fieldMessages.contentId },
		{ key: 'destinationAlias', label: fieldMessages.destinationAlias },
	],
	CREATE_FOLDER: [
		{ key: 'title', label: fieldMessages.folderTitle },
		{ key: 'parentContentId', label: fieldMessages.parentContentId },
	],
};

export function SuggestionActionBuilder({
	suggestionId,
	nextActionIndex,
	onAdd,
	onBack,
}: SuggestionActionBuilderProps): React.JSX.Element {
	const { formatMessage } = useIntl();

	const [actionType, setActionType] = useState<ActionType>('RENAME');
	// Field values keyed by field name. Shared keys (e.g. `title`, `contentId`)
	// intentionally persist across type switches so the user doesn't lose input.
	const [values, setValues] = useState<Record<string, string>>({});
	const [submitted, setSubmitted] = useState(false);

	const fields = FIELDS_BY_TYPE[actionType];

	const handleTypeChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setActionType(event.currentTarget.value as ActionType);
		setSubmitted(false);
	}, []);

	const handleFieldChange = useCallback(
		(key: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
			const { value } = event.currentTarget;
			setValues((current) => ({ ...current, [key]: value }));
		},
		[],
	);

	const missingKeys = useMemo(
		() =>
			fields.reduce<string[]>((missing, { key }) => {
				if (!String(values[key] ?? '').trim()) {
					missing.push(key);
				}
				return missing;
			}, []),
		[fields, values],
	);

	const handleSubmit = useCallback(() => {
		setSubmitted(true);
		if (missingKeys.length > 0) {
			return;
		}

		const get = (key: string) => String(values[key] ?? '').trim();

		const actionStepId = `${suggestionId}-${nextActionIndex}`;
		let action: RawAction;
		switch (actionType) {
			case 'RENAME':
				action = {
					actionStepId,
					parentActionStepId: null,
					actionType: 'RENAME',
					parameters: { contentId: get('contentId'), title: get('title') },
				};
				break;
			case 'MOVE':
				action = {
					actionStepId,
					parentActionStepId: null,
					actionType: 'MOVE',
					parameters: { contentId: get('contentId'), destinationAlias: get('destinationAlias') },
				};
				break;
			case 'CREATE_FOLDER':
				action = {
					actionStepId,
					parentActionStepId: null,
					actionType: 'CREATE_FOLDER',
					// Alias is derived locally; it only needs to be unique within the suggestion
					// so later actions could reference the new folder. A content picker is a follow-up.
					parameters: {
						alias: `new-folder-${nextActionIndex}`,
						parentContentId: get('parentContentId'),
						title: get('title'),
					},
				};
				break;
		}
		onAdd(action);
	}, [actionType, missingKeys, nextActionIndex, onAdd, suggestionId, values]);

	const typeOptions = useMemo(
		() => [
			{ name: 'action-type', value: 'RENAME', label: formatMessage(i18n.typeRename) },
			{ name: 'action-type', value: 'MOVE', label: formatMessage(i18n.typeMove) },
			{ name: 'action-type', value: 'CREATE_FOLDER', label: formatMessage(i18n.typeCreateFolder) },
		],
		[formatMessage],
	);

	return (
		<>
			<SidePanelBody>
				<Stack space="space.200">
					<Stack space="space.100">
						<Text weight="medium">{formatMessage(i18n.actionTypeLabel)}</Text>
						<RadioGroup options={typeOptions} value={actionType} onChange={handleTypeChange} />
					</Stack>
					<Stack space="space.150">
						{fields.map(({ key, label }) => {
							const fieldId = `suggestion-action-field-${key}`;
							const isInvalid = submitted && missingKeys.includes(key);
							return (
								<Stack key={key} space="space.050">
									<Label htmlFor={fieldId}>{formatMessage(label)}</Label>
									<Textfield
										id={fieldId}
										value={values[key] ?? ''}
										onChange={handleFieldChange(key)}
										isInvalid={isInvalid}
									/>
									{isInvalid && <ErrorMessage>{formatMessage(i18n.requiredError)}</ErrorMessage>}
								</Stack>
							);
						})}
					</Stack>
				</Stack>
			</SidePanelBody>

			<SidePanelFooter>
				<Flex direction="row" gap="space.100" justifyContent="end">
					<Button onClick={onBack}>{formatMessage(i18n.back)}</Button>
					<Button appearance="primary" onClick={handleSubmit}>
						{formatMessage(i18n.add)}
					</Button>
				</Flex>
			</SidePanelFooter>
		</>
	);
}

const i18n = defineMessages({
	actionTypeLabel: {
		id: 'space-suggestions.action-builder.action-type-label',
		defaultMessage: 'Action type',
		description: 'Label above the action type selector in the add-action form',
	},
	typeRename: {
		id: 'space-suggestions.action-builder.type-rename',
		defaultMessage: 'Rename',
		description: 'Option label for creating a rename action in the add-action form',
	},
	typeMove: {
		id: 'space-suggestions.action-builder.type-move',
		defaultMessage: 'Move',
		description: 'Option label for creating a move action in the add-action form',
	},
	typeCreateFolder: {
		id: 'space-suggestions.action-builder.type-create-folder',
		defaultMessage: 'Create folder',
		description: 'Option label for creating a create-folder action in the add-action form',
	},
	requiredError: {
		id: 'space-suggestions.action-builder.required-error',
		defaultMessage: 'This field is required',
		description: 'Validation error shown under a required field left empty in the add-action form',
	},
	back: {
		id: 'space-suggestions.action-builder.back-button',
		defaultMessage: 'Back',
		description: 'Label for the button that returns to the review list without adding an action',
	},
	add: {
		id: 'space-suggestions.action-builder.add-button',
		defaultMessage: 'Add',
		description: 'Label for the button that adds the built action to the suggestion',
	},
});
